import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { environment } from '@environments/environment';
import { ApiResponse } from '@app/_models';
import { Person } from '@app/_components/person';
import { retry, catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { AlertService } from '@app/_services';
import { SharedService } from './../_shared/shared.service';

@Injectable({ providedIn: 'root' })
export class PersonService {

	constructor
		(
			private http: HttpClient,
			private alertService: AlertService,
			private sharedService: SharedService
		) { }

	urlEndpoint = 'persons';

	add(person: Person): Observable<ApiResponse> {
		console.log('posting... ' + JSON.stringify(person));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, person);
	}

	update(person: Person): Observable<ApiResponse> {
		return this.http.patch<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, person);
	}

	delete(developmentId: number): Observable<ApiResponse> {
		return this.http.delete<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${developmentId}`);
	}

	findAll(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	findByFilter(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any): Observable<ApiResponse> {
		let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
		console.log('posting... ' + JSON.stringify(filters));
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));

	}

	findById(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	audit(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}/audit`).pipe(retry({ count: 2, delay: 1500 }));
	}


	prepareParamsFromPrimeNgDataTable(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any) {
		try {
			console.log(
				'  |  page:' + page
				+ '  |  size:' + size
				+ '  |  sortBy:' + sortBy
				+ '  |  sortOrder:' + sortOrder
				+ '  |  multiSortMeta:' + JSON.stringify(multiSortMeta)
				+ '  |  globalFilter:' + globalFilter
				+ '  |  filters:' + JSON.stringify(filters)
			);

			let params = new HttpParams();

			if (!isNaN(page)) { params = params.append('page', page); }
			if (!isNaN(size)) { params = params.append('size', size); }
			if (sortBy) { params = params.append('sort', sortBy + ',' + (sortOrder === 1 ? 'asc' : 'desc')); }
			if (multiSortMeta) {
				// if sortMode="multiple" in primeng table then multiSortMeta is used for ALL sorting (even just one column.)
				// Otherwise, single sorting is used above.
				// event.multiSortMeta:[{"field":"lastName","order":1},{"field":"firstName","order":1}]
				for (let i = 0; i < multiSortMeta.length; i++) {
					let json = JSON.parse(JSON.stringify(multiSortMeta[i]));
					params = params.append('sort', json.field + ',' + (json.order === 1 ? 'asc' : 'desc'));
				}
			}
			// globalFilter works, but did not implemnt in Spring but not hard to write custom findAll and specify certain columns to look.
			if (globalFilter) { params = params.append('filter', globalFilter); }

			// multiselect or input or whatever... these work slick.
			if (filters) {
				// key:status val:{"value":["A"],"matchMode":"in"}
				for (let i = 0, keys = Object.keys(filters), ii = keys.length; i < ii; i++) {
					console.log('key:' + keys[i] + ' val:' + JSON.stringify(filters[keys[i]]));
					params = params.append(keys[i], filters[keys[i]].value);
				}
			}
			return params;
		} catch (error) {
			console.log('prepareParamsFromPrimeNgDataTable()' + error);
		}
	}
	
	userRoles = [
		{ value: 1, name: 'General', color: '#DCDCDC' },
		{ value: 2, name: 'Employee', color: '#ffcccc' },
		{ value: 3, name: 'Guest', color: '#FFFF99' },
		{ value: 4, name: 'Buyer', color: '#FFFF99' },
		{ value: 5, name: 'Homeowner', color: '#FFFF99' },
		{ value: 6, name: 'Realtor', color: '#FFFF99' },
		{ value: 7, name: 'Developer', color: '#FFFF99' }
	];

	genders = [
		{ value: 'M', name: 'Male' },
		{ value: 'F', name: 'Female' }
	];
	
	suffixes = [
		{ value: 'Mr.', name: 'Mr.' },
		{ value: 'Mrs.', name: 'Mrs.' },
		{ value: 'Miss Dr.', name: 'Miss Dr.' },
		{ value: 'Hon.', name: 'Hon.' }
	];

	phoneTypes = [
		{ value: '1', name: 'Primary' },
		{ value: '2', name: 'Home' },
		{ value: '3', name: 'Mobile' },
		{ value: '4', name: 'Work' },
		{ value: '5', name: 'Fax' },
		{ value: '6', name: 'Pager' },
		{ value: '7', name: 'Other' },
	];

	emailTypes = [
		{ value: '1', name: 'Primary' },
		{ value: '2', name: 'Home' },
		{ value: '3', name: 'Work' },
		{ value: '4', name: 'Tax' },
		{ value: '5', name: 'Other' },
	];

	addressTypes = [
		{ value: '1', name: 'Business' },
		{ value: '2', name: 'Home' },
		{ value: '3', name: 'Mailing' },
	];

	visibilities = [
		{ value: 1, name: 'Company' },
		{ value: 2, name: 'Team' },
		{ value: 3, name: 'Personal' },
		{ value: 1, name: '' },
		{ value: 4, name: 'Customer/Owner' }
	];

	statuses = [
		{ value: 2, name: 'Proposed' },
		{ value: 0, name: 'Active' },
		{ value: 3, name: 'On Hold' },
		{ value: 1, name: 'Complete' },
		{ value: 4, name: 'Canceled' }
	];
	
	socials = [
		{ value: "fb", name: 'Facebook' },
		{ value: "ig", name: 'Instagram' },
		{ value: "li", name: 'LinkedIn' },
		{ value: "tw", name: 'Twitter' },
		{ value: "tr", name: 'Truth' }
	];

	// list of countries and states https://gist.github.com/ebaranov/41bf38fdb1a2cb19a781
	countries: any[] =
		[
			{ name: 'Canada', value: 'CA' },
			{ name: 'United States', value: 'US' },
			{ name: '', value: '' },
			{ name: 'Afghanistan', value: 'AF' },
			{ name: 'Aland Islands', value: 'AX' },
			{ name: 'Albania', value: 'AL' },
			{ name: 'Algeria', value: 'DZ' },
			{ name: 'American Samoa', value: 'AS' },
			{ name: 'AndorrA', value: 'AD' },
			{ name: 'Angola', value: 'AO' },
			{ name: 'Anguilla', value: 'AI' },
			{ name: 'Antarctica', value: 'AQ' },
			{ name: 'Antigua and Barbuda', value: 'AG' },
			{ name: 'Argentina', value: 'AR' },
			{ name: 'Armenia', value: 'AM' },
			{ name: 'Aruba', value: 'AW' },
			{ name: 'Australia', value: 'AU' },
			{ name: 'Austria', value: 'AT' },
			{ name: 'Azerbaijan', value: 'AZ' },
			{ name: 'Bahamas', value: 'BS' },
			{ name: 'Bahrain', value: 'BH' },
			{ name: 'Bangladesh', value: 'BD' },
			{ name: 'Barbados', value: 'BB' },
			{ name: 'Belarus', value: 'BY' },
			{ name: 'Belgium', value: 'BE' },
			{ name: 'Belize', value: 'BZ' },
			{ name: 'Benin', value: 'BJ' },
			{ name: 'Bermuda', value: 'BM' },
			{ name: 'Bhutan', value: 'BT' },
			{ name: 'Bolivia', value: 'BO' },
			{ name: 'Bosnia and Herzegovina', value: 'BA' },
			{ name: 'Botswana', value: 'BW' },
			{ name: 'Bouvet Island', value: 'BV' },
			{ name: 'Brazil', value: 'BR' },
			{ name: 'British Indian Ocean Territory', value: 'IO' },
			{ name: 'Brunei Darussalam', value: 'BN' },
			{ name: 'Bulgaria', value: 'BG' },
			{ name: 'Burkina Faso', value: 'BF' },
			{ name: 'Burundi', value: 'BI' },
			{ name: 'Cambodia', value: 'KH' },
			{ name: 'Cameroon', value: 'CM' },

			{ name: 'Cape Verde', value: 'CV' },
			{ name: 'Cayman Islands', value: 'KY' },
			{ name: 'Central African Republic', value: 'CF' },
			{ name: 'Chad', value: 'TD' },
			{ name: 'Chile', value: 'CL' },
			{ name: 'China', value: 'CN' },
			{ name: 'Christmas Island', value: 'CX' },
			{ name: 'Cocos (Keeling) Islands', value: 'CC' },
			{ name: 'Colombia', value: 'CO' },
			{ name: 'Comoros', value: 'KM' },
			{ name: 'Congo', value: 'CG' },
			{ name: 'Congo, The Democratic Republic of the', value: 'CD' },
			{ name: 'Cook Islands', value: 'CK' },
			{ name: 'Costa Rica', value: 'CR' },
			{ name: 'Cote D\'Ivoire', value: 'CI' },
			{ name: 'Croatia', value: 'HR' },
			{ name: 'Cuba', value: 'CU' },
			{ name: 'Cyprus', value: 'CY' },
			{ name: 'Czech Republic', value: 'CZ' },
			{ name: 'Denmark', value: 'DK' },
			{ name: 'Djibouti', value: 'DJ' },
			{ name: 'Dominica', value: 'DM' },
			{ name: 'Dominican Republic', value: 'DO' },
			{ name: 'Ecuador', value: 'EC' },
			{ name: 'Egypt', value: 'EG' },
			{ name: 'El Salvador', value: 'SV' },
			{ name: 'Equatorial Guinea', value: 'GQ' },
			{ name: 'Eritrea', value: 'ER' },
			{ name: 'Estonia', value: 'EE' },
			{ name: 'Ethiopia', value: 'ET' },
			{ name: 'Falkland Islands (Malvinas)', value: 'FK' },
			{ name: 'Faroe Islands', value: 'FO' },
			{ name: 'Fiji', value: 'FJ' },
			{ name: 'Finland', value: 'FI' },
			{ name: 'France', value: 'FR' },
			{ name: 'French Guiana', value: 'GF' },
			{ name: 'French Polynesia', value: 'PF' },
			{ name: 'French Southern Territories', value: 'TF' },
			{ name: 'Gabon', value: 'GA' },
			{ name: 'Gambia', value: 'GM' },
			{ name: 'Georgia', value: 'GE' },
			{ name: 'Germany', value: 'DE' },
			{ name: 'Ghana', value: 'GH' },
			{ name: 'Gibraltar', value: 'GI' },
			{ name: 'Greece', value: 'GR' },
			{ name: 'Greenland', value: 'GL' },
			{ name: 'Grenada', value: 'GD' },
			{ name: 'Guadeloupe', value: 'GP' },
			{ name: 'Guam', value: 'GU' },
			{ name: 'Guatemala', value: 'GT' },
			{ name: 'Guernsey', value: 'GG' },
			{ name: 'Guinea', value: 'GN' },
			{ name: 'Guinea-Bissau', value: 'GW' },
			{ name: 'Guyana', value: 'GY' },
			{ name: 'Haiti', value: 'HT' },
			{ name: 'Heard Island and Mcdonald Islands', value: 'HM' },
			{ name: 'Holy See (Vatican City State)', value: 'VA' },
			{ name: 'Honduras', value: 'HN' },
			{ name: 'Hong Kong', value: 'HK' },
			{ name: 'Hungary', value: 'HU' },
			{ name: 'Iceland', value: 'IS' },
			{ name: 'India', value: 'IN' },
			{ name: 'Indonesia', value: 'ID' },
			{ name: 'Iran, Islamic Republic Of', value: 'IR' },
			{ name: 'Iraq', value: 'IQ' },
			{ name: 'Ireland', value: 'IE' },
			{ name: 'Isle of Man', value: 'IM' },
			{ name: 'Israel', value: 'IL' },
			{ name: 'Italy', value: 'IT' },
			{ name: 'Jamaica', value: 'JM' },
			{ name: 'Japan', value: 'JP' },
			{ name: 'Jersey', value: 'JE' },
			{ name: 'Jordan', value: 'JO' },
			{ name: 'Kazakhstan', value: 'KZ' },
			{ name: 'Kenya', value: 'KE' },
			{ name: 'Kiribati', value: 'KI' },
			{ name: 'Korea, Democratic People\'S Republic of', value: 'KP' },
			{ name: 'Korea, Republic of', value: 'KR' },
			{ name: 'Kuwait', value: 'KW' },
			{ name: 'Kyrgyzstan', value: 'KG' },
			{ name: 'Lao People\'S Democratic Republic', value: 'LA' },
			{ name: 'Latvia', value: 'LV' },
			{ name: 'Lebanon', value: 'LB' },
			{ name: 'Lesotho', value: 'LS' },
			{ name: 'Liberia', value: 'LR' },
			{ name: 'Libyan Arab Jamahiriya', value: 'LY' },
			{ name: 'Liechtenstein', value: 'LI' },
			{ name: 'Lithuania', value: 'LT' },
			{ name: 'Luxembourg', value: 'LU' },
			{ name: 'Macao', value: 'MO' },
			{ name: 'Macedonia, The Former Yugoslav Republic of', value: 'MK' },
			{ name: 'Madagascar', value: 'MG' },
			{ name: 'Malawi', value: 'MW' },
			{ name: 'Malaysia', value: 'MY' },
			{ name: 'Maldives', value: 'MV' },
			{ name: 'Mali', value: 'ML' },
			{ name: 'Malta', value: 'MT' },
			{ name: 'Marshall Islands', value: 'MH' },
			{ name: 'Martinique', value: 'MQ' },
			{ name: 'Mauritania', value: 'MR' },
			{ name: 'Mauritius', value: 'MU' },
			{ name: 'Mayotte', value: 'YT' },
			{ name: 'Mexico', value: 'MX' },
			{ name: 'Micronesia, Federated States of', value: 'FM' },
			{ name: 'Moldova, Republic of', value: 'MD' },
			{ name: 'Monaco', value: 'MC' },
			{ name: 'Mongolia', value: 'MN' },
			{ name: 'Montserrat', value: 'MS' },
			{ name: 'Morocco', value: 'MA' },
			{ name: 'Mozambique', value: 'MZ' },
			{ name: 'Myanmar', value: 'MM' },
			{ name: 'Namibia', value: 'NA' },
			{ name: 'Nauru', value: 'NR' },
			{ name: 'Nepal', value: 'NP' },
			{ name: 'Netherlands', value: 'NL' },
			{ name: 'Netherlands Antilles', value: 'AN' },
			{ name: 'New Caledonia', value: 'NC' },
			{ name: 'New Zealand', value: 'NZ' },
			{ name: 'Nicaragua', value: 'NI' },
			{ name: 'Niger', value: 'NE' },
			{ name: 'Nigeria', value: 'NG' },
			{ name: 'Niue', value: 'NU' },
			{ name: 'Norfolk Island', value: 'NF' },
			{ name: 'Northern Mariana Islands', value: 'MP' },
			{ name: 'Norway', value: 'NO' },
			{ name: 'Oman', value: 'OM' },
			{ name: 'Pakistan', value: 'PK' },
			{ name: 'Palau', value: 'PW' },
			{ name: 'Palestinian Territory, Occupied', value: 'PS' },
			{ name: 'Panama', value: 'PA' },
			{ name: 'Papua New Guinea', value: 'PG' },
			{ name: 'Paraguay', value: 'PY' },
			{ name: 'Peru', value: 'PE' },
			{ name: 'Philippines', value: 'PH' },
			{ name: 'Pitcairn', value: 'PN' },
			{ name: 'Poland', value: 'PL' },
			{ name: 'Portugal', value: 'PT' },
			{ name: 'Puerto Rico', value: 'PR' },
			{ name: 'Qatar', value: 'QA' },
			{ name: 'Reunion', value: 'RE' },
			{ name: 'Romania', value: 'RO' },
			{ name: 'Russian Federation', value: 'RU' },
			{ name: 'RWANDA', value: 'RW' },
			{ name: 'Saint Helena', value: 'SH' },
			{ name: 'Saint Kitts and Nevis', value: 'KN' },
			{ name: 'Saint Lucia', value: 'LC' },
			{ name: 'Saint Pierre and Miquelon', value: 'PM' },
			{ name: 'Saint Vincent and the Grenadines', value: 'VC' },
			{ name: 'Samoa', value: 'WS' },
			{ name: 'San Marino', value: 'SM' },
			{ name: 'Sao Tome and Principe', value: 'ST' },
			{ name: 'Saudi Arabia', value: 'SA' },
			{ name: 'Senegal', value: 'SN' },
			{ name: 'Serbia and Montenegro', value: 'CS' },
			{ name: 'Seychelles', value: 'SC' },
			{ name: 'Sierra Leone', value: 'SL' },
			{ name: 'Singapore', value: 'SG' },
			{ name: 'Slovakia', value: 'SK' },
			{ name: 'Slovenia', value: 'SI' },
			{ name: 'Solomon Islands', value: 'SB' },
			{ name: 'Somalia', value: 'SO' },
			{ name: 'South Africa', value: 'ZA' },
			{ name: 'South Georgia and the South Sandwich Islands', value: 'GS' },
			{ name: 'Spain', value: 'ES' },
			{ name: 'Sri Lanka', value: 'LK' },
			{ name: 'Sudan', value: 'SD' },
			{ name: 'Suriname', value: 'SR' },
			{ name: 'Svalbard and Jan Mayen', value: 'SJ' },
			{ name: 'Swaziland', value: 'SZ' },
			{ name: 'Sweden', value: 'SE' },
			{ name: 'Switzerland', value: 'CH' },
			{ name: 'Syrian Arab Republic', value: 'SY' },
			{ name: 'Taiwan, Province of China', value: 'TW' },
			{ name: 'Tajikistan', value: 'TJ' },
			{ name: 'Tanzania, United Republic of', value: 'TZ' },
			{ name: 'Thailand', value: 'TH' },
			{ name: 'Timor-Leste', value: 'TL' },
			{ name: 'Togo', value: 'TG' },
			{ name: 'Tokelau', value: 'TK' },
			{ name: 'Tonga', value: 'TO' },
			{ name: 'Trinidad and Tobago', value: 'TT' },
			{ name: 'Tunisia', value: 'TN' },
			{ name: 'Turkey', value: 'TR' },
			{ name: 'Turkmenistan', value: 'TM' },
			{ name: 'Turks and Caicos Islands', value: 'TC' },
			{ name: 'Tuvalu', value: 'TV' },
			{ name: 'Uganda', value: 'UG' },
			{ name: 'Ukraine', value: 'UA' },
			{ name: 'United Arab Emirates', value: 'AE' },
			{ name: 'United Kingdom', value: 'GB' },

			{ name: 'United States Minor Outlying Islands', value: 'UM' },
			{ name: 'Uruguay', value: 'UY' },
			{ name: 'Uzbekistan', value: 'UZ' },
			{ name: 'Vanuatu', value: 'VU' },
			{ name: 'Venezuela', value: 'VE' },
			{ name: 'Viet Nam', value: 'VN' },
			{ name: 'Virgin Islands, British', value: 'VG' },
			{ name: 'Virgin Islands, U.S.', value: 'VI' },
			{ name: 'Wallis and Futuna', value: 'WF' },
			{ name: 'Western Sahara', value: 'EH' },
			{ name: 'Yemen', value: 'YE' },
			{ name: 'Zambia', value: 'ZM' },
			{ name: 'Zimbabwe', value: 'ZW' }
		];
}
